import { useState, useRef, useEffect } from 'react';

import { Input } from '@/components/ui/input';
import { Check, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TimePickerProps {
  value: string;
  onValueChange: (value: string) => void;
  interval?: number; // minutes
  start?: string; // HH:mm 24h
  end?: string; // HH:mm 24h
  placeholder?: string;
  className?: string;
}

function generateTimes(interval: number, start: string, end: string) {
  const list: { value: string; label: string }[] = [];
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let current = sh * 60 + sm;
  const endMin = eh * 60 + em;
  while (current <= endMin) {
    const h = Math.floor(current / 60);
    const m = current % 60;
    const value = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    const ampmH = ((h + 11) % 12) + 1;
    const period = h >= 12 ? 'PM' : 'AM';
    const label = `${ampmH.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} ${period}`;
    list.push({ value, label });
    current += interval;
  }
  return list;
}

export const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onValueChange,
  interval = 15,
  start = '07:00',
  end = '21:00',
  placeholder = 'Select time',
  className,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const options = generateTimes(interval, start, end);

  const currentLabel = options.find((o) => o.value === value)?.label ?? '';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <Input
          type="text"
          value={value ? currentLabel : ''}
          placeholder={placeholder}
          className={cn('pr-10 cursor-pointer', className)}
          readOnly
          onClick={() => setOpen(!open)}
        />
        <Clock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
      </div>
      
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60">
          <div 
            className="overflow-y-auto max-h-60"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: '#d1d5db transparent'
            }}
          >
            {options.map((opt) => (
              <div
                key={opt.value}
                onClick={() => {
                  onValueChange(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  "flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors text-sm",
                  value === opt.value && "bg-gray-100"
                )}
              >
                <Check className={cn('mr-2 h-4 w-4', value === opt.value ? 'opacity-100' : 'opacity-0')} />
                {opt.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Alias export for backwards-compatibility with previous naming
export { TimePicker as TimeSelect }; 